// atoms/profile.ts
import { atom } from "recoil";
import { Profile, ProfileStats } from "@/types/entities";
import { getAPI } from "@/api";

// Constants
const PROFILE_KEY = "profile";

// State atom for a profile
export const profileState = atom<Profile | null>({
  key: PROFILE_KEY,
  default: null,
});

/**
 * Fetch a profile based on a user key.
 * @param {string} userKey - The key of the user.
 * @return {Promise<Profile | null>} The user's profile or null if not found.
 */
export async function fetchProfile(userKey: string): Promise<Profile | null> {
  const API = getAPI();
  try {
    return await API.profile.fetchProfile(userKey);
  } catch (error) {
    console.error("Failed to fetch profile:", error);
    return null;
  }
}

/**
 * Create or update a profile.
 * @param {string} authKey - The authentication key of the user.
 * @param {Profile} data - The profile data to set.
 * @return {Profile} The profile data that was set.
 */
export async function createOrUpdateProfile(
  authKey: string,
  data: Partial<Profile>
): Promise<Profile> {
  const API = getAPI();
  // validate to check all set
  if (!data.username) {
    throw new Error("Username is required");
  }

  try {
    return await API.profile.createOrUpdateProfile(authKey, data);
  } catch (error) {
    console.error("Failed to create or update profile:", error);
    throw error;
  }
}

/**
 * Delete a profile based on a user key.
 * @param {string} userKey - The key of the user.
 * @return {Promise<{ success: boolean; message: string }>} The result of the deletion operation.
 */
export async function deleteProfile(
  userKey: string
): Promise<{ success: boolean; message: string }> {
  const API = getAPI();
  try {
    return await API.profile.deleteProfile(userKey);
  } catch (error) {
    console.error("Failed to delete profile:", error);
    throw error;
  }
}

/**
 * Fetch a profile stats based on a user key.
 * @param {string} userKey - The key of the user.
 * @return {Promise<ProfileStats | null>} The user's profile stats or null if not found.
 */
export async function fetchProfileStats(userKey: string): Promise<ProfileStats | null> {
  const API = getAPI();
  try {
    return await API.profile.fetchProfileStats(userKey);
  } catch (error) {
    console.error("Failed to fetch profile stats:", error);
    return null;
  }
}